import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  ModuleEvent, 
  ModuleWarning, 
  IntegrityIssue, 
  crossCheckAfterEvent, 
  systemIntegrityCheck,
  detectUploadModules,
  getModuleLabel,
} from '@/lib/moduleSync';
import { toast } from 'sonner';

export const useModuleSync = () => {
  const [warnings, setWarnings] = useState<ModuleWarning[]>([]);
  const [integrityIssues, setIntegrityIssues] = useState<IntegrityIssue[]>([]);
  const [isCheckingIntegrity, setIsCheckingIntegrity] = useState(false);
  const [lastIntegrityCheck, setLastIntegrityCheck] = useState<Date | null>(null);

  // Emit an event and run cross-module checks
  const emitEvent = useCallback(async (event: ModuleEvent) => {
    try {
      const newWarnings = await crossCheckAfterEvent(supabase, event);
      
      if (newWarnings.length > 0) {
        setWarnings(prev => {
          // Deduplicate by module+relatedModule+recordId
          const existing = new Set(prev.map(w => `${w.module}-${w.relatedModule}-${w.recordId}`));
          const unique = newWarnings.filter(w => !existing.has(`${w.module}-${w.relatedModule}-${w.recordId}`));
          return [...prev, ...unique];
        });

        // Show toast for each warning
        for (const w of newWarnings) {
          const icon = w.severity === 'critical' ? '🔴' : w.severity === 'warning' ? '⚠️' : 'ℹ️';
          toast.warning(`${icon} ${w.message}`, {
            duration: 6000,
            action: w.actionLabel ? {
              label: w.actionLabel,
              onClick: () => {},
            } : undefined,
          });
        }
      }

      // Upload intelligence
      if (event.type === 'upload' && event.data?.name) {
        const detectedModules = detectUploadModules(event.data.name);
        if (detectedModules.length > 0) {
          const moduleNames = detectedModules.map(getModuleLabel).join(', ');
          toast.info(`📎 Dokument könnte zu ${moduleNames} gehören`, {
            duration: 8000,
          });
        }
      }
    } catch (error) {
      console.error('Module sync error:', error);
    }
  }, []);

  // Run full system integrity check
  const runIntegrityCheck = useCallback(async () => {
    setIsCheckingIntegrity(true);
    try {
      const issues = await systemIntegrityCheck(supabase);
      setIntegrityIssues(issues);
      setLastIntegrityCheck(new Date());
      return issues;
    } catch (error) {
      console.error('Integrity check failed:', error);
      toast.error('Integritätsprüfung fehlgeschlagen');
      return [];
    } finally {
      setIsCheckingIntegrity(false);
    }
  }, []);

  // Dismiss a specific warning
  const dismissWarning = useCallback((warningId: string) => {
    setWarnings(prev => prev.filter(w => w.id !== warningId));
  }, []);

  // Dismiss all warnings
  const dismissAllWarnings = useCallback(() => {
    setWarnings([]);
  }, []);

  // Auto-create a booking for a missing financial entry
  const autoCreateBooking = useCallback(async (
    projectId: string,
    description: string,
    amount: number,
    category: string = 'Automatisch erstellt'
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase.from('bookings').insert({
        type: 'ausgabe',
        amount,
        description,
        category,
        project_id: projectId,
        created_by: user.id,
        is_planned: true,
        booking_date: new Date().toISOString().split('T')[0],
      });

      if (error) throw error;
      toast.success(`Buchung "${description}" automatisch erstellt`);
      return true;
    } catch (error) {
      console.error('Auto-create booking failed:', error);
      toast.error('Automatische Buchung fehlgeschlagen');
      return false;
    }
  }, []);

  return {
    warnings,
    integrityIssues,
    isCheckingIntegrity,
    lastIntegrityCheck,
    emitEvent,
    runIntegrityCheck,
    dismissWarning,
    dismissAllWarnings,
    autoCreateBooking,
  };
};
